import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { IRISH_COUNTIES } from "../utils/irishCounties";

const Register = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    companyName: "",
    phone: "",
    taxId: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "Ireland",
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    setLoading(true);
    const result = await register(formData);

    if (result.success) {
      navigate("/login");
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen px-4 py-12 sm:py-16">
      <div className="container mx-auto max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <h1 className="heading-metallic mb-2 text-3xl sm:text-4xl">Trade Account Application</h1>
          <p className="text-gray-400">Apply for a wholesale trade account to access our full catalog and pricing</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Account Information */}
            <div>
              <h3 className="heading-metallic-soft text-xl mb-4">Account Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">First Name *</label>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="input-field w-full" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Last Name *</label>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="input-field w-full" required />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email Address *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} className="input-field w-full" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Password *</label>
                  <input type="password" name="password" value={formData.password} onChange={handleChange} className="input-field w-full" minLength="8" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Confirm Password *</label>
                  <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} className="input-field w-full" minLength="8" required />
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div className="pt-6 border-t border-gray-700">
              <h3 className="heading-metallic-soft text-xl mb-4">Business Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Company Name *</label>
                  <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} className="input-field w-full" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Phone Number *</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="input-field w-full" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Tax ID / VAT Number</label>
                  <input type="text" name="taxId" value={formData.taxId} onChange={handleChange} className="input-field w-full" />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="pt-6 border-t border-gray-700">
              <h3 className="heading-metallic-soft text-xl mb-4">Business Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Address Line 1 *</label>
                  <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleChange} className="input-field w-full" required />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Address Line 2</label>
                  <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleChange} className="input-field w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">City *</label>
                  <input type="text" name="city" value={formData.city} onChange={handleChange} className="input-field w-full" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">County *</label>
                  <select name="state" value={formData.state} onChange={handleChange} className="input-field w-full" required>
                    <option value="">-- Select County --</option>
                    {IRISH_COUNTIES.map((county) => (
                      <option key={county} value={county}>
                        {county}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Postal Code *</label>
                  <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} className="input-field w-full" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Country *</label>
                  <select name="country" value={formData.country} onChange={handleChange} className="input-field w-full" required>
                    <option value="Ireland">Ireland</option>
                    <option value="United Kingdom">United Kingdom</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="pt-6">
              <button type="submit" disabled={loading} className="btn-primary w-full">
                {loading ? "Submitting Application..." : "Submit Application"}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              Already have an account?{" "}
              <Link to="/login" className="text-gold-500 hover:text-gold-400">
                Sign In
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;
